<template>
  <div 
    class="inistate-lite layout"
    :class="{'inistate-lite--design': isDesignMode}"
  >
    <template v-if="contentReady">
      <div 
        class="layout__sidenav" 
        :class="{'layout__sidenav--collapsed': !isSidenavStatic}"
      >
        <div class="sidenav__header">
          <div class="sidenav__header-top">
            <div class="sidenav__header-top-left">
              <div class="sidenav__workspace" v-if="isAdminUser && isDesignMode">
                <input 
                  class="fts-form-control" 
                  :class="{'form-control--error': $v.formData.workspaceName && $v.formData.workspaceName.$error}" 
                  v-model.trim="formData.workspaceName" 
                  @blur="saveWorkspaceName" 
                  @keydown.enter="saveWorkspaceName" 
                  ref="workspace-name" 
                  v-if="isWorkspaceNameEditable" 
                />
                
                <div 
                  class="sidenav__workspace-toggle sidenav__workspace-toggle--editable"  
                  @click="setWorkspaceNameEditable(true)"
                  v-else
                >
                  <div class="sidenav__workspace-label">{{workspaceName || "Workspace"}}</div>
                </div>

                <div class="sidenav__workspace-hint" v-if="$v.formData.workspaceName && $v.formData.workspaceName.$error">
                  <div v-if="$v.formData.workspaceName.format === false">
                    <div>Allow alphabet and numeric only.</div>
                    <div>
                      Allow special characters
                      <span
                        v-for="(x, index) in workspaceNameSpecialCharacters"
                        :key="`wnsc-${index}`"
                      >{{x}}</span>
                    </div>
                    <div class="sidenav__workspace-error">
                      Workspace name cannot contain invalid characters.
                    </div>
                  </div>
                  
                  <div class="sidenav__workspace-error" v-else-if="$v.formData.workspaceName.required === false">
                    Workspace Name is required.
                  </div>

                  <div class="sidenav__workspace-error" v-else>
                    Workspace Name is invalid.
                  </div>
                </div> 
              </div>

              <div class="sidenav__workspace" v-else>
                <div class="sidenav__workspace-toggle">
                  <div class="sidenav__workspace-label">{{workspaceName || "Workspace"}}</div>
                </div>
              </div>

              <fts-dropdown-select 
                :prevent-overflow-padding="{ left: 12, right: 12 }" 
                :offset="[0, 3]" 
                placement="bottom-end" 
                append-toggle-class="sidenav__workspace-menu-toggle" 
                append-toggle-active-class="sidenav__workspace-menu-toggle--active" 
                content-class="dropdown-menu dropdown-menu-w-xs" 
                @show="showWorkspaceOptions" 
                @selected="selectWorkspaceOption"
              >
                <template #toggle="{ isShow }">
                  <fts-tippy
                    content="Switch workspace" 
                    :append-trigger-class="isShow ? 'sidenav__toggle sidenav__toggle--active' : 'sidenav__toggle'" 
                  >
                    <span class="material-icons-outlined">expand_more</span>
                  </fts-tippy>
                </template>

                <template #menu="{ select }">
                  <div class="dropdown-filter">
                    <div class="fts-form-control-filter">
                      <input
                        type="text"
                        class="fts-form-control"
                        v-model.trim="workspaceSearch"
                        placeholder="Search"
                        ref="workspaceSearch"
                      />
                      <div class="search-icon">
                        <fts-spinner :size="14" show v-if="workspacesSearching" />
                        <font-awesome-icon :icon="['fas', 'search']" fixed-width v-else />
                      </div>
                    </div>
                  </div>

                  <template v-if="workspaceOptionList && workspaceOptionList.length">
                    <div 
                      v-for="workspace in workspaceOptionList" 
                      :key="workspace.id" 
                      class="dropdown-option" 
                      @click="select(workspace)" 
                    >
                      <div>{{workspace.name}}</div>
                    </div>
                  </template>

                  <fts-spinner height="84px" :size="24" v-else-if="workspacesLoading" />

                  <div class="dropdown-option-not-found" v-else>
                    No matching results
                  </div>

                  <div class="dropdown-divider"></div>

                  <div 
                    class="dropdown-option" 
                    @click="select('manage')" 
                    v-if="isAdminUser && manageWorkspaceInMenu"
                  >
                    <div class="dropdown-option-icon">
                      <font-awesome-icon :icon="['fas', 'cog']" fixed-width />
                    </div>
                    <div>Manage workspace</div>
                  </div>

                  <div class="dropdown-option" @click="select('create')">
                    <div class="dropdown-option-icon">
                      <font-awesome-icon :icon="['fas', 'plus']" fixed-width />
                    </div>
                    <div>Create a new workspace</div>
                  </div>
                </template>
              </fts-dropdown-select>
            </div>

            <fts-tippy 
              content="Close sidenav" 
              append-trigger-class="sidenav__toggle" 
              @trigger:click="toggleSidenav(false, true)"
            >
              <span class="material-symbols-outlined">dock_to_right</span>
            </fts-tippy>
          </div>

          <div class="sidenav__divider"></div>

          <!-- portals: above the modules (PL-11), so above the MODULES row too, which heads the list
               below it; each opens in a new tab; nothing when there is none -->
          <fts-portal-nav
            :workspace="workspace"
            variant="lite"
            v-if="!isLiteLobby && !hasNoActiveWorkspace"
          />

          <div class="sidenav__header-bottom" v-if="!isLiteLobby && !hasNoActiveWorkspace">
            <template v-if="isSidenavSearchEnabled">
              <div class="fts-form-control-filter sidenav__filter">
                <input 
                  class="fts-form-control" 
                  placeholder="Search" 
                  v-model.trim="sidenavSearch" 
                  ref="module-search"
                />
                <div class="search-icon">
                  <span class="material-icons-outlined">search</span>
                </div>
              </div>

              <div class="sidenav__action-container">
                <fts-tippy 
                  content="Close search" 
                  append-trigger-class="sidenav__action-btn" 
                  @trigger:click="toggleSidenavSearch(false)" 
                  v-if="isSidenavSearchEnabled"
                >
                  <span class="material-icons-outlined">close</span>
                </fts-tippy>
              </div>
            </template>

            <template v-else>
              <div class="sidenav__module-label">MODULES</div>

              <div class="sidenav__action-container">
                <fts-tippy
                  content="Search" 
                  append-trigger-class="sidenav__action-btn" 
                  @trigger:click="toggleSidenavSearch(true)"
                >
                  <span class="material-icons-outlined">search</span>
                </fts-tippy>

                <fts-tippy 
                  content="Add module" 
                  :append-trigger-class="workspaceLoading ? 'sidenav__action-btn sidenav__action-btn--disabled' : 'sidenav__action-btn'" 
                  @trigger:click="createModule" 
                  v-if="isDesignMode"
                >
                  <span class="material-icons-outlined" data-testid="lite-open-module-hub">settings</span>
                </fts-tippy>

                <fts-tippy 
                  content="Collapse all views" 
                  append-trigger-class="sidenav__action-btn" 
                  @trigger:click="collapseAllViews" 
                  v-if="isAnySidebarMenuOpen"
                >
                  <span class="material-icons-outlined">unfold_less</span>
                </fts-tippy>
              </div>
            </template>
          </div>
        </div>

        <div class="sidenav__body inistate-scroller" data-v-step="modules-listings">
          <fts-sidenav-module
            :filter="sidenavSearch"
            :is-design-mode="isDesignMode"
            class="sidenav__module-container"
            ref="sidenav-module"
            v-if="!isLiteLobby && !hasNoActiveWorkspace"
          />
        </div>

        <div class="sidenav__footer">
          <div class="sidenav__divider"></div>

          <div class="topnav__control">
            <fts-tippy 
              v-for="item in controls" 
              :key="item.id" 
              :content="item.name"
            >
              <div 
                class="topnav__control-item" 
                :class="control === item.id ? 
                  (
                    item.id === 'design' ?
                      'topnav__control-item--active' : 
                      'topnav__control-item--active-outline'
                  ) : ''
                " 
                :data-testid="`lite-control-${item.id}`"
                @click="setControl(item.id)" 
              >
                <span :class="item.iconClass">{{item.iconName}}</span>
                <div class="control-item-label" v-if="item.id === 'design'">
                  {{item.name}}
                </div>
              </div>
            </fts-tippy>
          </div>

          <div class="sidenav__divider"></div>

          <div class="sidenav__profile-container">
            <!-- The avatar is the way into settings, as in the Client shell; the gear beside it
                 goes to the same place. -->
            <fts-profile 
              :profile="profile" 
              :profile-photo-size="44" 
              class="sidenav__profile"
              role="button"
              tabindex="0"
              @click.native="openUserProfileModal"
              @keydown.native.enter="openUserProfileModal"
            >
              <template #profileName="{ userProfileName, userProfileUsername}">
                <div class="sidenav__profile-name">{{userProfileName}}</div>
                <div class="sidenav__profile-username">{{userProfileUsername}}</div>
              </template>
            </fts-profile>

            <fts-tippy 
              content="Account settings" 
              append-trigger-class="sidenav-settings" 
              @trigger:click="openUserProfileModal" 
            >
              <span class="material-icons-outlined">settings</span>
            </fts-tippy>
          </div>
        </div>
      </div>

      <div class="layout__main" data-name="lite-layout-main">
        <div class="layout__topnav">
          <div class="topnav__left">
            <fts-tippy 
              content="Lock sidenav open" 
              append-trigger-class="sidenav__toggle" 
              class="topnav_sidenav-toggle"
              @trigger:click="toggleSidenav(true, true)" 
              data-click-stop 
              v-if="!isSidenavStatic"
            >
              <span class="material-symbols-outlined">dock_to_right</span>
            </fts-tippy>

            <div class="topnav__title-wrapper" style="margin-left: 0.75rem;" v-if="$route.path === '/lite/module-hub'">
              <div class="topnav__title">Module Hub</div>
            </div>

            <div class="topnav__title-wrapper" v-else-if="isLiteLobby">
              <div class="topnav__title">Lobby</div>
            </div>

            <div class="topnav__title-wrapper" v-else-if="isUsersPage">
              <div class="topnav__title">User management</div>
            </div>

            <div class="topnav__title-wrapper" v-else-if="!isSidenavStatic && control == 'preview'">
              <div class="topnav__logo">
                <img :src="sidenavLogo" />
              </div>
              <div class="topnav__title">{{workspaceName}}</div>
            </div>

            <div class="topnav__breadcrumb" v-if="breadcrumb">
              <template v-if="isDesignMode">
                <div class="topnav__breadcrumb-item-wrapper">
                  <div
                    class="topnav__breadcrumb-item" 
                    :class="{ 'topnav__breadcrumb-item--active': breadcrumb.module.active }" 
                    @click="navigateMiniStudio({ moduleId: breadcrumb.module.id })"
                  >
                    <fts-label-group 
                      :item="breadcrumb.module" 
                      emoji 
                      :emoji-size="12" 
                    ></fts-label-group>

                    <fts-dropdown-select 
                      append-to-body 
                      overlay 
                      placement="bottom" 
                      prevent-overflow-alt-axis 
                      prevent-overflow-tether-off 
                      :offset="[0, 6]" 
                      append-toggle-class="topnav__breadcrumb-dropdown-toggle" 
                      append-toggle-active-class="topnav__breadcrumb-dropdown-toggle--expanded" 
                      content-class="dropdown-menu dropdown-menu-w-sm dropdown-menu--sticky" 
                      :options="workspaceModuleOptions" 
                      data-click-stop 
                      @selected="evt => navigateView('module', evt)" 
                      @show="focusRef('module-search')" 
                      @hide="workspaceModuleSearch = null" 
                      @click.native.stop 
                    >
                      <div slot="toggle" class="topnav__breadcrumb-item-icon">
                        <span class="material-icons-outlined">keyboard_arrow_down</span>  
                      </div>

                      <div slot="menu-header" class="dropdown-filter">
                        <div class="fts-form-control-filter">
                          <input 
                            class="fts-form-control" 
                            v-model.trim="workspaceModuleSearch" 
                            placeholder="Search" 
                            ref="module-search" 
                          />

                          <div class="search-icon">
                            <font-awesome-icon :icon="['fas', 'search']" fixed-width />
                          </div>
                        </div>
                      </div>

                      <div 
                        slot="option" 
                        slot-scope="{ select, option }" 
                        class="dropdown-option" 
                        @click="select(option)"
                      >
                        <fts-label-group :item="option" emoji :emoji-size="12" />
                      </div>
                    </fts-dropdown-select>
                  </div>
                </div>

                <template v-if="breadcrumb.listing">
                  <div class="topnav__breadcrumb-divider">/</div>
                  <div class="topnav__breadcrumb-item-wrapper">
                    <div
                      class="topnav__breadcrumb-item" 
                      :class="{ 'topnav__breadcrumb-item--active': breadcrumb.listing.active }" 
                      @click="openViewDesign({ listingId: breadcrumb.listing.id })"
                    >
                      <fts-label-group 
                        :item="breadcrumb.listing" 
                        emoji 
                        :emoji-size="12" 
                      ></fts-label-group>

                      <fts-dropdown-select
                        append-to-body 
                        overlay 
                        placement="bottom" 
                        prevent-overflow-alt-axis 
                        prevent-overflow-tether-off 
                        :offset="[0, 6]" 
                        append-toggle-class="topnav__breadcrumb-dropdown-toggle" 
                        append-toggle-active-class="topnav__breadcrumb-dropdown-toggle--expanded" 
                        content-class="dropdown-menu dropdown-menu-w-sm dropdown-menu--sticky" 
                        :options="moduleListingOptions" 
                        data-click-stop 
                        @selected="evt => navigateView('listing', evt)" 
                        @show="focusRef('listing-search')" 
                        @hide="moduleListingSearch = null"
                        @click.native.stop
                      >
                        <div slot="toggle" class="topnav__breadcrumb-item-icon">
                          <span class="material-icons-outlined">keyboard_arrow_down</span>  
                        </div>

                        <div slot="menu-header" class="dropdown-filter">
                          <div class="fts-form-control-filter">
                            <input 
                              class="fts-form-control" 
                              v-model.trim="moduleListingSearch" 
                              placeholder="Search" 
                              ref="listing-search" 
                            />

                            <div class="search-icon">
                              <font-awesome-icon :icon="['fas', 'search']" fixed-width />
                            </div>
                          </div>
                        </div>

                        <div 
                          slot="option" 
                          slot-scope="{ select, option }" 
                          class="dropdown-option" 
                          @click="select(option)"
                        >
                          <fts-label-group :item="option" emoji :emoji-size="12" />
                        </div>
                      </fts-dropdown-select>
                    </div>
                  </div>
                </template>
                
                <fts-tippy 
                  content="Add view" 
                  append-trigger-class="topnav__breadcrumb-action"
                  @trigger:click="openViewDesign({ new: true })"
                >
                  <font-awesome-icon :icon="['fas', 'plus']" />
                </fts-tippy>
              </template>

              <template v-else>
                <fts-dropdown-select 
                  append-to-body 
                  overlay 
                  placement="bottom-start" 
                  prevent-overflow-alt-axis 
                  prevent-overflow-tether-off 
                  :offset="[0, 0]" 
                  :append-toggle-class="
                    breadcrumb.module.active ? 
                      'topnav__breadcrumb-item topnav__breadcrumb-dropdown-toggle topnav__breadcrumb-item--active' : 
                      'topnav__breadcrumb-item topnav__breadcrumb-dropdown-toggle'
                  " 
                  append-toggle-active-class="topnav__breadcrumb-dropdown-toggle--expanded" 
                  content-class="dropdown-menu dropdown-menu-w-sm dropdown-menu--sticky" 
                  :options="workspaceModuleOptions" 
                  class="topnav__breadcrumb-item-wrapper" 
                  data-click-stop 
                  @selected="evt => navigateView('module', evt)" 
                  @show="focusRef('module-search')" 
                  @hide="workspaceModuleSearch = null"
                >
                  <template slot="toggle">
                    <fts-label-group 
                      :item="breadcrumb.module" 
                      emoji 
                      :emoji-size="12" 
                    ></fts-label-group>

                    <div class="topnav__breadcrumb-item-icon">
                      <span class="material-icons-outlined">keyboard_arrow_down</span>
                    </div>
                  </template>

                  <div slot="menu-header" class="dropdown-filter">
                    <div class="fts-form-control-filter">
                      <input 
                        class="fts-form-control" 
                        v-model.trim="workspaceModuleSearch" 
                        placeholder="Search" 
                        ref="module-search" 
                      />

                      <div class="search-icon">
                        <font-awesome-icon :icon="['fas', 'search']" fixed-width />
                      </div>
                    </div>
                  </div>

                  <div 
                    slot="option" 
                    slot-scope="{ select, option }" 
                    class="dropdown-option" 
                    @click="select(option)"
                  >
                    <fts-label-group :item="option" emoji :emoji-size="12" />
                  </div>
                </fts-dropdown-select>

                <template v-if="breadcrumb.listing">
                  <div class="topnav__breadcrumb-divider">/</div>
                  <fts-dropdown-select 
                    append-to-body 
                    overlay 
                    placement="bottom-start" 
                    prevent-overflow-alt-axis 
                    prevent-overflow-tether-off 
                    :offset="[0, 0]" 
                    append-toggle-class="topnav__breadcrumb-item topnav__breadcrumb-dropdown-toggle topnav__breadcrumb-item--active" 
                    append-toggle-active-class="topnav__breadcrumb-dropdown-toggle--expanded" 
                    content-class="dropdown-menu dropdown-menu-w-sm dropdown-menu--sticky" 
                    :options="moduleListingOptions" 
                    class="topnav__breadcrumb-item-wrapper" 
                    data-click-stop 
                    @selected="evt => navigateView('listing', evt)" 
                    @show="focusRef('listing-search')" 
                    @hide="moduleListingSearch = null"
                  >
                    <template slot="toggle" >
                      <fts-label-group 
                        :item="breadcrumb.listing" 
                        emoji 
                        :emoji-size="12" 
                      ></fts-label-group>

                      <div class="topnav__breadcrumb-item-icon">
                        <span class="material-icons-outlined">keyboard_arrow_down</span>  
                      </div>
                    </template>

                    <div slot="menu-header" class="dropdown-filter">
                      <div class="fts-form-control-filter">
                        <input 
                          class="fts-form-control" 
                          v-model.trim="moduleListingSearch" 
                          placeholder="Search" 
                          ref="listing-search" 
                        />

                        <div class="search-icon">
                          <font-awesome-icon :icon="['fas', 'search']" fixed-width />
                        </div>
                      </div>
                    </div>

                    <div 
                      slot="option" 
                      slot-scope="{ select, option }" 
                      class="dropdown-option" 
                      @click="select(option)"
                    >
                      <fts-label-group :item="option" emoji :emoji-size="12" />
                    </div>
                  </fts-dropdown-select>
                </template>
              </template>
            </div>
          </div>

          <div class="topnav__right" v-if="!isLiteLobby && !hasNoActiveWorkspace">
            <!-- demo mode (internal only) -->
            <fts-tippy
              v-if="isInternalIdentity"
              :content="demoMode ? 'Demo mode ON — click to restore alpha/beta features' : 'Demo mode — hide all alpha/beta features'"
            >
              <fts-btn
                text
                icon
                class="demo-mode-toggle"
                :class="{ 'demo-mode-toggle--active': demoMode }"
                @click="toggleDemoMode"
              >
                <font-awesome-icon :icon="['far', demoMode ? 'eye-slash' : 'eye']" />
              </fts-btn>
            </fts-tippy>
            <fts-dropdown-select
              append-to-body
              overlay
              placement="bottom-start" 
              prevent-overflow-alt-axis 
              prevent-overflow-tether-off 
              :offset="[0, 0]" 
              content-class="notification-content notification-dropdown-menu" 
              @show="openNotification"
              @selected="openNotificationItem"
              data-v-step="welcome"
            >
              <fts-tippy slot="toggle" content="Notifications">
                <fts-btn text icon>
                  <font-awesome-icon :icon="['far', 'bell']" />
                </fts-btn>
              </fts-tippy>

              <template #menu="{select}">
                <!-- notification:header -->
                <div class="notification-dropdown__header">
                  <div class="notification-header-title">Notifications</div>
                </div>
                <!-- notification:content -->
                <div class="notification-dropdown__content inistate-scroller">
                  <template v-if="notifications && notifications.length">
                    <fts-notification-content
                      v-for="notification in notifications"
                      :key="notification.id"
                      :notification="notification"
                      @select="select"
                    />
                  </template>
                  <!-- infinite loading -->
                  <infinite-loading v-if="notifReady" @infinite="infiniteHandler">
                    <fts-spinner
                      show
                      :size="24"
                      slot="spinner"
                      class="notification-loader-spinner"
                    />
                    <div slot="no-more" class="notification-loader-blank">
                      No older notifications
                    </div>
                    <div slot="no-results" class="notification-loader-blank">
                      No older notifications
                    </div>
                  </infinite-loading>
                </div>
              </template>
            </fts-dropdown-select>
            <!-- content-notification reader (docs/notification-content-proposal.md §8.1) -->
            <fts-notification-reader
              @read="onNotificationRead"
              @open-entry="p => onFormOpen({ entryId: p.id, moduleId: p.vectorId })"
            />

            <fts-dropdown-select
              :options="navigationOptions"
              @selected="evt => navigatePage(evt.routeName)" 
              v-if="navigationOptions.length"
            >
              <fts-btn slot="toggle" text icon>
                <font-awesome-icon :icon="['fas', 'ellipsis-v']" />
              </fts-btn>
            </fts-dropdown-select>
          </div>
        </div>

        <div class="layout__content">
          <div v-if="workspaceLoading"></div>
          <div v-else-if="hasNoActiveWorkspace"></div>
          <template v-else>
            <router-view :key="routePath" />

            <div class="fab-control" v-if="!isSidenavStatic">
              <div
                v-for="item in controls" 
                :key="item.id" 
                class="fab-control-item" 
                :style="{
                  top: isFloatingMenuOpen ? item.floatingPosition.top : 0,
                  left: isFloatingMenuOpen ? item.floatingPosition.left : 0,
                  opacity: isFloatingMenuOpen ? 1 : 0
                }" 
                @click="setControl(item.id);isFloatingMenuOpen = false;" 
              >
                <span :class="item.iconClass">{{item.iconName}}</span>
                <div class="fab-control-item-label">{{item.name}}</div>
              </div>

              <fts-btn 
                icon 
                color="primary" 
                class="fab-control-toggle" 
                @click="isFloatingMenuOpen = !isFloatingMenuOpen"
              >
                <font-awesome-icon :icon="['fas', 'times']" v-if="isFloatingMenuOpen" />
                <span class="material-icons-outlined" v-else-if="control === 'design'">edit</span>
                <span class="material-icons-outlined" v-else-if="control === 'preview'">phone_iphone</span>
                <span class="material-icons-outlined" v-else>desktop_windows</span>
              </fts-btn>
            </div>
          </template>
        </div>
      </div>
      <!-- confirmation -->
      <v-dialog />
      <!-- user profile -->
      <fts-user-profile :is-lite="true" ref="ftsUserProfile" />
      <!-- create workspace modal -->
      <fts-workspace-create-modal ref="workspaceCreateModal" workspace-type="lite" @created="onWorkspaceCreated" />
      <!-- workspace modal -->
      <fts-workspace-modal ref="ftsWorkspaceModal" />
      <!--pdf view-->
      <fts-document-modal ref="documentModal" />
      <!-- htmljs document view (engine === 'htmljs'): separate viewer, legacy path untouched -->
      <fts-html-js-document-modal ref="htmlJsDocumentModal" />
      <!-- module onboard modal -->
      <fts-module-onboard-modal
        client
        description="You have created the system successfully."
        @closed="startTour"
        ref="ftsModuleOnboardModal"
      />
      <!-- confirmation -->
      <fts-confirmation global />
      <!-- tour -->
      <v-tour
        name="liteTour"
        :steps="tourSteps()"
        :callbacks="{ 'onFinish': endTour, 'onStop': endTour }"
        :options="tourOptions"></v-tour>
      <!-- form skip confirmation -->
      <fts-form-skip-confirmation ref="ftsFormSkipConfirmation" />
    </template>

    <fts-spinner
      full-screen
      variant="rive"
      v-else
    />

    <fts-spinner
      full-screen
      variant="rive"
      class="client-loader"
      v-if="workspaceLoading"
    />
  </div>
</template>

<script>
import Vue from "vue";
import InfiniteLoading from "vue-infinite-loading";
import VueCookies from "vue-cookies";
Vue.use(VueCookies);
import VueTour from 'vue-tour';
require("vue-tour/dist/vue-tour.css");
Vue.use(VueTour);
import signalr from "./components/store/signalr";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);
import { required } from "vuelidate/lib/validators";

import webAppConfig from "./components/commons/webApp.config";
import commons from "./components/commons/commons";
import { billingReturnSection, clearBillingReturn, readBillingReturn } from "./components/commons/billing";
import { buildDocumentRequest, sendWithTokenRefreshCallback, PDF_DOCUMENT_PATH, DOCUMENT_POST_CONTENT_TYPE } from "./components/commons/documentRequest";
import tours from "./components/commons/tours";
import { classificationStore } from "./components/store/classificationStore";
import { clientStore } from "./components/store/clientStore";
import { authorizationStore } from "./components/store/authorizationStore";
import { applyDisplay } from "./components/commons/readability";
import { settingsStageAllowed } from "./components/settings/settingsStage";
import { settingsSectionFor } from "./components/settings/settingsNav";
import { store as canvasDesignStore } from "./components/store/canvasDesignStore";
import LocalStorageService from './components/store/LocalStorageService';
import designValidator from './components/commons/designValidator';

import FTSSidenavModule from './components/lite/FTSSidenavModule';
import FTSPortalNav from './components/portals/FTSPortalNav';
import FTSWorkspaceModal from "./components/design/FTSWorkspaceModal";
import FTSDropdownSelect from "./components/elements/FTSDropdownSelect";
import FTSUserProfile from "./components/elements/FTSUserProfile";
import FTSProfile from "./components/elements/FTSProfile";
import FTSTippy from "./components/elements/FTSTippy";
import FTSSpinner from "./components/elements/FTSSpinner";
import FTSDocumentModal from "./components/elements/FTSDocumentModal";
import FTSHtmlJsDocumentModal from "./components/elements/FTSHtmlJsDocumentModal";
import FTSNotificationContent, { isContentNotification } from "./components/elements/notification/FTSNotificationContent";
import { approvalRequestIdOf } from "./components/approvals/approvalApi";
import FTSNotificationReader from "./components/elements/notification/FTSNotificationReader";
import FTSConfirmation from "./components/elements/confirmation/FTSConfirmation";
import FTSModuleOnboardModal from './components/design/FTSModuleOnboardModal';
import FTSFormSkipConfirmation from './components/elements/form/FTSFormSkipConfirmation.vue';
import FTSBtn from "./components/elements/FTSBtn";
import FTSLabelGroup from './components/elements/FTSLabelGroup';
import FTSModal from "./components/elements/FTSModal";
import FTSWorkspaceCreateModal from './components/client/FTSWorkspaceCreateModal';

export default {
  metaInfo() {
    return { title: this.metaTitle };
  },
  components: {
    InfiniteLoading,
    "fts-sidenav-module": FTSSidenavModule,
    "fts-portal-nav": FTSPortalNav,
    "fts-workspace-modal": FTSWorkspaceModal,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-user-profile": FTSUserProfile,
    "fts-profile": FTSProfile,
    "fts-tippy": FTSTippy,
    "fts-spinner": FTSSpinner,
    "fts-document-modal": FTSDocumentModal,
    "fts-html-js-document-modal": FTSHtmlJsDocumentModal,
    "fts-notification-content": FTSNotificationContent,
    "fts-notification-reader": FTSNotificationReader,
    "fts-confirmation": FTSConfirmation,
    "fts-module-onboard-modal": FTSModuleOnboardModal,
    "fts-form-skip-confirmation": FTSFormSkipConfirmation,
    "fts-btn": FTSBtn,
    "fts-label-group": FTSLabelGroup,
    "fts-modal": FTSModal,
    "fts-workspace-create-modal": FTSWorkspaceCreateModal,
  },
  data: () => ({
    prefReady: false,
    contentReady: false,
    isSidenavStatic: true,
    formData: {},
    /* workspace */
    workspaceLoading: false,
    workspacesLoading: false,
    workspacesSearching: false,
    workspaceSearch: "",
    workspaceOptionList: [],
    workspaceOptionRequest: 0,
    workspaceContextSearch: null,
    isWorkspaceNameEditable: false,
    previousWorkspaceId: null,
    hasNoActiveWorkspace: false,
    /* context */
    contextOptionList: [],
    /* notification */
    unreadCount: 0,
    notifications: [],
    notifCurrentPage: 0,
    notifReady: false,
    /* pre workspaces */
    preWorkspaces: [],
    // run as
    isRunAs: false,
    // real-time changes
    workspaceRTCStatus: "",
    userRTCStatus: "",
    inactivityStartTime: null,
    workspaceVersionInterval: null,
    workspaceChangedTimeout: null,
    userChangedTimeout: null,
    currentUserChangedTimeout: null,
    control: "web",
    tourOptions:  {
      labels: {
        buttonSkip: 'Skip',
        // buttonPrevious: 'Previous',
        buttonNext: 'Next',
        buttonStop: 'Finish'
      }
    },
    sidenavSearch: null,
    isSidenavSearchEnabled: false,
    workspaceModuleSearch: null,
    moduleListingSearch: null,
    isFloatingMenuOpen: false
  }),
  validations() {
    let formData = {};

    if (this.isWorkspaceNameEditable) {
      formData = commons.getFormValidators([{
        type: 0,
        fieldName: "workspaceName",
        required: true,
        validator: {
          format: designValidator.workspaceNameFormat,
        }
      }]);
    }

    return { formData };
  },
  methods: {
    toggleDemoMode() {
      authorizationStore.dispatch("setDemoMode");
    },
    setControl(type) {
      if (this.isLiteLobby && type !== "web") {
        this.setControlState("web");
        return;
      }

      if (type === this.control) return;

      let routeToPush = null;
      const prevControl = this.control;
      const getLiteRoute = (moduleId, listingId) => {
        const route = { name: "lite" };
        if (moduleId) {
          route.params = { moduleId: commons.encodeids(moduleId) };
        }

        if (listingId) {
          route.query = { l: listingId };
        }
        
        return route;
      }

      // switching from preview
      if (prevControl === "preview") {
        routeToPush = getLiteRoute(this.moduleId, this.listId);
      }
      // switching from design
      if (prevControl === "design") {
        clientStore.dispatch("setLiteDesignMode", false);

        if (
          this.$route.name === "lite-studio" && 
          type !== "preview"
        ) {
          let moduleId = null;
          let listingId = null;

          if (this.$route.params.moduleId) {
            const decodeModuleId = commons.decodeids(this.$route.params.moduleId);
            moduleId = commons.encodeids(decodeModuleId);

            if (decodeModuleId) {
              const firstOrDefaultMenu = commons.getFirstOrDefaultMenu(clientStore.getters.workspaceMenus, decodeModuleId);
              if (firstOrDefaultMenu && firstOrDefaultMenu.listingId) {
                listingId = firstOrDefaultMenu.listingId;
              }
            }
          }

          routeToPush = getLiteRoute(moduleId, listingId);
        }
      }
      // switching to preview
      if (type === "preview" && this.$route.name !== "lite-preview") {
        routeToPush = { name: "lite-preview" };
      }
      // switching to design
      if (type === "design") {
        clientStore.dispatch("setLiteDesignMode", true);

        const hasWorkspaceModules = Boolean(this.workspaceModules && this.workspaceModules.length);
        if (this.isAdminUser && !hasWorkspaceModules && this.$route.name !== "lite-module-hub") {
          routeToPush = { name: "lite-module-hub" };
        }
      }
      // push router
      if (routeToPush) {
        this.$router.push(routeToPush);
      }

      LocalStorageService.setLiteMode(webAppConfig.baseServer, type);
      this.control = type;
    },
    setControlState(type) {
      LocalStorageService.setLiteMode(webAppConfig.baseServer, type);
      this.control = type;
    },
    navigatePage(routeName) {
      this.$router.push({ name: routeName });
    },
    navigateView(source, data) {
      const showError = () => {
        this.$toasted.error("Oops! Failed to navigate. Please retry.");
      };

      if (source == "listing" && data.script) {
        try {
          eval(data.script)
        } catch(err) {
          showError();
        }

        return;
      }

      let route = { name: "lite" };

      if (source == "module") {
        if (!data || !data.id) {
          showError();
          return;
        }

        route.params = {
          moduleId: commons.encodeids(data.id)
        };

        if (Array.isArray(data.menus)) {
          const firstOrDefaultMenu = data.menus.find(menu => !menu.script);

          if (firstOrDefaultMenu) {
            route.query = { l: firstOrDefaultMenu.id };
          }
        }
      } else if (source == "listing") {
        route.params = {
          moduleId: commons.encodeids(this.moduleId)
        };

        route.query = { l: data.id };
      } else {
        showError();
        return;
      }

      this.$router.push(route);
    },
    async navigateMiniStudio(payload) {
      if (payload && payload.moduleId) {
        let route = {
          name: "lite-studio",
          params: { moduleId: commons.encodeids(payload.moduleId) }
        }

        const targetRoute = this.$router.resolve(route).route;
        if (targetRoute.fullPath !== this.$route.fullPath) {
          await this.$router.push(route);
        }
      }
    },
    toggleSidenav(val, isPreference = true) {
      this.isSidenavStatic = val;
      
      if (isPreference) {
        LocalStorageService.setPreference({
          ...LocalStorageService.getPreference(),
          collapse: !this.isSidenavStatic
        });
      }
    },
    toggleSidenavSearch(mode) {
      this.isSidenavSearchEnabled = mode;

      if (mode) {
        this.$nextTick(() => {
          const el = this.$refs["module-search"];
          if (el) {
            el.focus();
          }
        });
      } else {
        this.sidenavSearch = null;
      }
    },
    collapseAllViews() {
      this.$refs['sidenav-module'].collapseAllMenus();
    },
    // workspace
    async setDefaultWorkspaceContext() {
      const workspaceContexts = await clientStore.dispatch("getWorkspaceContexts", {
        search: "" 
      });

      if (workspaceContexts && workspaceContexts.length > 0) {
        clientStore.dispatch("setWorkspaceContext", workspaceContexts[0]);
      }
    },
    async loadWorkspaceCache(workspaceId) {
      const lsWorkspaceData = LocalStorageService.getWorkspace(webAppConfig.baseServer, workspaceId);
      if (lsWorkspaceData && lsWorkspaceData.id) {
        try {
          clientStore.dispatch("setWorkspace", { workspace: lsWorkspaceData });
          
          if (this.workspace.hasContext && !this.isWorkspaceContext) {
            await this.setDefaultWorkspaceContext();
          }

          return true;
        } catch(err) {
          LocalStorageService.clearWorkspace(webAppConfig.baseServer);
        }
      }

      return false;
    },
    async loadWorkspace(payload, option) {
      this.sidenavSearch = null;
      let isInitial = !!(option && option.isInitial);
      let requestError = null;
      const isModulePayload = !!(payload && payload.moduleId);

      const _loadWorkspace = async (requestPayload) => {
        try {
          const loadWorkspaceAction =
            requestPayload && requestPayload.moduleId
              ? "loadWorkspaceByModuleId"
              : "loadWorkspace";
          await clientStore.dispatch(loadWorkspaceAction, requestPayload);
          this.hasNoActiveWorkspace = false;
          const signalrConnectionState = signalr.getConnectionState();
          if (this.previousWorkspaceId != this.workspaceId || signalrConnectionState === "disconnected") {
            const maxRetries = 3;
            for (let retryCount = 1; retryCount <= maxRetries; retryCount++) {
              try {
                await signalr.setupCommunication(this.workspaceId);
                break;
              } catch(signalrError) {
                console.error(`Failed to setup communication. Attempt ${retryCount} of ${maxRetries}`, signalrError);
                await new Promise(resolve => setTimeout(resolve, 3000));
                if (retryCount === maxRetries) {
                  console.error("Failed to setup communication", signalrError);
                }
              }
            }
          }
          requestError = null;

          if (this.workspaceLobby && this.$route.name !== "lite-lobby") {
            this.$router.replace({ name: "lite-lobby" });
          }
        } catch(err) {
          console.error("Failed to load workspace", err);
          requestError = err;
        }
      }

      await _loadWorkspace(payload);

      /* SS06178/SS06180: a background refresh keeps the workspace it already has. Redirecting and
         reloading "a" workspace here - with the remembered id gone - landed the user in the first
         workspace of their list after an intermittent 500. */
      if (requestError && payload && payload.background) {
        throw requestError;
      }

      if (requestError) {
        if (isModulePayload) {
          this.$toasted.global.error({
            response: requestError.response,
            message: "Oops! We couldn't open the requested page. Redirecting..."
          });

          if (this.$route.name === "lite" && this.$route.params && this.$route.params.moduleId) {
            await this.$router.replace({
              name: "lite",
              query: _.cloneDeep(this.$route.query)
            });
          }

          clientStore.dispatch("resetModule");
        } else if (!isInitial) {
          this.$toasted.global.error(
            requestError,
            "Oops! Unable to load the workspace at the moment. Redirecting..."
          );

          if (this.$route.name != "dashboard") {
            this.$router.replace({ name: "dashboard" });
          }

          clientStore.dispatch("resetModule");
        }

        await _loadWorkspace();

        if (!this.isLiteMode) {
          this.$router.replace({ name: "dashboard" });
        }
      }

      if (requestError) {
        throw requestError;
      }
    },
    async loadPreWorkspaces() {
      try {
        this.preWorkspaces = [{
          id: this.workspace.id,
          name: this.workspace.name,
          shortName: commons.getWorkspaceShortName(this.workspace.name)
        }];

        const res = await clientStore.dispatch("getWorkspaces", {
          search: ""
        });

        this.preWorkspaces = this.preWorkspaces.concat(
          ...res
            .filter(x => x.id != this.workspace.id)
            .slice(0, 2)
            .map(x => ({
              id: x.id,
              name: x.name,
              shortName: commons.getWorkspaceShortName(x.name)
            }))
        );
      } catch(err) {
        console.error(err);
      }
    },
    async showWorkspaceOptions() {
      this.workspacesLoading = true;
      this.workspaceSearch = "";
      await this.loadWorkspaceOptions(this.workspaceSearch);
      this.workspacesLoading = false;
      this.$nextTick(() => {
        this.$refs.workspaceSearch && this.$refs.workspaceSearch.focus();
      });
    },
    /**
     * Fills the workspace switcher's list. Only the newest request may: opening fetches every
     * workspace and typing fetches the matches 500 ms later, so a slow opening response used to
     * land last and put the full list back under the search text (see Client.vue).
     */
    async loadWorkspaceOptions(search) {
      const request = ++this.workspaceOptionRequest;
      const list = await clientStore.dispatch("getWorkspaces", { search });
      if (request !== this.workspaceOptionRequest) return;
      this.workspaceOptionList = list;
      this.workspacesSearching = false;
    },
    async selectWorkspace(workspace) {
      if (workspace.id != this.workspace.id) {
        try {
          this.workspaceLoading = true;

          if (this.isRunAs) {
            this.isRunAs = false;
            authorizationStore.dispatch("setRunAs", { workspaceId: workspace.id });
          }

          if (this.isWorkspaceNameEditable) {
            this.isWorkspaceNameEditable = false;
          }

          clientStore.dispatch("resetModule");
          // const hasCache = await this.loadWorkspaceCache(workspace.id);
          // if (hasCache) {
          //   this.workspaceLoading = false;
          // }

          await this.loadWorkspace({
            id: workspace.id,
            client: true,
          });

          if (
            (
              this.$route.name !== "lite" &&
              this.$route.name !== "lite-preview"
            ) ||
            (
              this.$route.name == "lite" &&
              this.$route.params &&
              this.$route.params.moduleId
            )
          ) {
            this.$router.push({ name: this.isLiteMode ? "lite" : "dashboard" });
          }

          // load default workspace context
          if (this.workspace.hasContext && !this.isWorkspaceContext) {
            await this.setDefaultWorkspaceContext();
          }
        } catch(err) {
          console.error(err);
          location.reload(); // to-do error
        } finally {
          this.workspaceLoading = false;
        }
      }
    },
    /**
     * Back from Stripe Checkout (`?billing=…&ws=…` on the server query, which the hash router
     * leaves alone): open that workspace's Plan & Credits tab so it can confirm the outcome.
     * Mirrors Client.openBillingReturn, billing emails' `open` and AutoCount Full's return included.
     */
    async openBillingReturn() {
      const ret = readBillingReturn(window.location.search);
      if (!ret) return;
      if (ret.kind === "open") clearBillingReturn();
      try {
        if (ret.workspaceId && ret.workspaceId != this.workspaceId) {
          await this.selectWorkspace({ id: ret.workspaceId });
        }
        if (ret.kind === "open" && ret.workspaceId && ret.workspaceId != this.workspaceId) return;
        await this.selectWorkspaceOption("manage", { section: "subscription", settingsSection: billingReturnSection(ret) });
      } catch (err) {
        console.error("billing return", err);
      }
    },
    async selectWorkspaceOption(item, modalOptions) {
      if (item == "manage") {
        // Workspace settings: everyone lands in the settings area (GA); this dialog is the
        // kill switch's fallback. Same seam as Client.vue. W-25.
        if (settingsStageAllowed() && !(modalOptions && modalOptions.legacy)) {
          const section = settingsSectionFor(modalOptions);
          this.$router.push({ name: "settings", params: { section } }).catch(() => {});
          return;
        }
        const res = await this.$refs.ftsWorkspaceModal.show({ id: this.workspaceId, ...(modalOptions || {}) });
        if (res && res.name) {
          clientStore.dispatch("setWorkspaceName", res.name);
          let preWorkspace = this.preWorkspaces.find(x => x.id == res.id);
          preWorkspace && (preWorkspace.name = res.name);
        } else if (res && res.type == "delete") {
          this.workspaceLoading = true;
          let selectedWorkspaceId = null;
          LocalStorageService.clearWorkspace(webAppConfig.baseServer);

          if (Array.isArray(this.preWorkspaces)) {
            const preWorkspace = this.preWorkspaces.find(x => x.id != res.id);
            selectedWorkspaceId = preWorkspace && preWorkspace.id;
          }

          if (!selectedWorkspaceId) {
            selectedWorkspaceId = await clientStore.dispatch("getFirstOrDefaultWorkspaceId");
          }

          if (selectedWorkspaceId) {
            await this.selectWorkspace({ id: selectedWorkspaceId });
          } else {
            location.reload();
          }
        } else if (res && res.id) {
          await this.selectWorkspace({ id: res.id });
        }
      } else if (item == "create") {
        this.$refs.workspaceCreateModal.open();
      } else {
        this.selectWorkspace(item);
      }
    },
    async showContextOptions() {
      this.workspaceContextSearch = "";
      this.contextOptionList = await clientStore.dispatch(
        "getWorkspaceContexts",
        { search: this.workspaceContextSearch}
      );

      this.$nextTick(() => {
        this.$refs.workspaceContextSearch.focus();
      });
    },
    selectWorkspaceContext(res) {
      clientStore.dispatch("setWorkspaceContext", res);
      window.eventBus.$emit("listing:refresh");
    },
    setWorkspaceNameEditable(value) {
      if (value) {
        this.$set(this.formData, "workspaceId", this.workspaceId);
        this.$set(this.formData, "workspaceName", this.workspaceName);
        this.isWorkspaceNameEditable = value;
        this.$v.$reset();
        this.$nextTick(() => {
          const el = this.$refs["workspace-name"];
          if (el) {
            el.focus();
          }
        });
      } else {
        this.$delete(this.formData, "workspaceId");
        this.$delete(this.formData, "workspaceName");
        this.isWorkspaceNameEditable = value;
      }
    },
    async saveWorkspace(payload) {
      if (!payload || !payload.id) return;

      let workspaceData = null;

      try {
        const res = await canvasDesignStore.dispatch("retrieveWorkspaceInformation", payload.id);

        if (res && res.data) {
          workspaceData = res.data;
        } else {
          this.$toasted.error("Failed to update workspace");
          return;
        }
      } catch(error) {
        this.$toasted.global.error(error);
        return;
      }

      workspaceData.id = payload.id;

      if (payload.name) {
        workspaceData.name = payload.name;
      }

      try {
        await canvasDesignStore.dispatch("updateWorkspaceInformation", workspaceData);
      } catch(error) {
        this.$toasted.global.error(error);
      }
    },
    saveWorkspaceName() {
      if (!this.isWorkspaceNameEditable) {
        return;
      }

      const { workspaceId, workspaceName } = this.formData;

      this.$v.formData.workspaceName.$touch();

      if (this.$v.formData.workspaceName.$error) {
        return;
      }

      if (workspaceId === this.workspaceId && workspaceName === this.workspaceName) {
        this.setWorkspaceNameEditable(false);
        return;
      }

      clientStore.dispatch("setWorkspaceName", workspaceName);
      this.saveWorkspace({ id: workspaceId, name: workspaceName });
      this.setWorkspaceNameEditable(false);
    },
    async onWorkspaceCreated(workspaceId) {
      try {
        await this.loadWorkspace({ id: workspaceId, client: true });

        if (this.workspaceId != workspaceId) {
          throw new Error("Workspace was created, but we couldn't switch to it. Please refresh and try again.");
        }

        if (this.isAdminUser) {
          await this.loadDesign({ workspaceId: this.workspaceId });
          clientStore.dispatch("setLiteDesignMode", true);
          this.setControlState("design");

          try {
            const moduleId = await this.createInitialWorkspaceModule();
            this.$router.push({
              name: "lite-studio",
              params: { moduleId: commons.encodeids(moduleId) }
            });
          } catch (moduleError) {
            console.error("Failed to create initial workspace module:", moduleError);
            this.$toasted.global.error({
              response: moduleError.response,
              message: "Workspace created, but unable to create the initial module."
            });
            this.$router.push({ name: "lite-module-hub" });
          }
        }
      } catch(error) {
        console.error(error);
        this.$toasted.global.error(error);
      }
    },
    // form
    onFormOpen(payload) {
      window.eventBus.$emit("listViewForm:open", {
        id: payload.entryId,
        moduleId: payload.moduleId,
        listingId: payload.listingId,
        activityId: payload.activityId
      });
    },
    // design
    async loadDesign({ workspaceId, moduleId }) {
      if (workspaceId) {
        await canvasDesignStore.dispatch("loadWorkspace", { id: workspaceId });
      } 
      
      if (moduleId) {
        canvasDesignStore.dispatch("resetVector");
        canvasDesignStore.dispatch("initialCanvas");
        await canvasDesignStore.dispatch("loadVector", moduleId);
      }
    },
    initializeCreateModuleCanvas(name) {
      canvasDesignStore.dispatch("resetVector");
      canvasDesignStore.dispatch("initialVector");
      canvasDesignStore.dispatch("initialCanvas");
      canvasDesignStore.dispatch("updateVectorCanvas", {
        ...canvasDesignStore.state.canvas,
        standardActivities: commons.standards
      });
      canvasDesignStore.dispatch("updateModule", {
        ...canvasDesignStore.getters.vector,
        name
      });
    },
    async createInitialWorkspaceModule() {
      this.initializeCreateModuleCanvas("Untitled");

      const res = await canvasDesignStore.dispatch("saveVector");
      const moduleId = res && res.data && res.data.id;

      if (!moduleId) {
        throw new Error("Module was created without an id.");
      }

      await canvasDesignStore.dispatch("loadVector", moduleId);
      await canvasDesignStore.dispatch("loadWorkspace", { id: this.workspaceId });
      await clientStore.dispatch("loadWorkspace", {
        id: this.workspaceId,
        forceReload: true,
        preserveMenuState: true
      });

      return moduleId;
    },
    async openViewDesign(args = {}) {
      if (this.$route.name !== "lite-studio") {
        const moduleId = this.breadcrumb && this.breadcrumb.module
          ? this.breadcrumb.module.id
          : null;

        if (!moduleId) return;

        try {
          await this.navigateMiniStudio({ moduleId });
        } catch (error) {
          if (this.$route.name !== "lite-studio") return;
        }
      }

      await this.$nextTick();
      window.eventBus.$emit("open-designer", args);
    },
    // user profile 
    async openUserProfileModal() {
      // Same door as Client.vue: Settings › You, or the dialog when the kill switch is thrown or
      // the user is held in the lobby (lite-lobby lets no other route in, so the dialog is their
      // only Log out).
      if (settingsStageAllowed() && !this.workspaceLobby) {
        this.$router.push({ name: "settings", params: { section: "profile" } }).catch(() => {});
        return;
      }
      await this.$refs.ftsUserProfile.show();
    },
    // notification
    async infiniteHandler($state) {
      const res = await this.loadNotification();
      if (res) {
        this.notifCurrentPage += 1;
        $state.loaded();
      } else {
        $state.complete();
      }
    },
    async openNotification() {
      // SS06219: list only once the "unreceived" clear has landed. That call re-saves every
      // unreceived reading, and a list query running alongside it can skip a row being re-saved -
      // the newest rows, the ones the bell is opened for (AWT #646 showed one of two fresh routine
      // rows; on 132 the parallel pair lost a row in 2 of 18 rounds, the list alone in 0 of 12).
      // The loader mounts behind notifReady, so every opening also starts it afresh.
      this.notifReady = false;
      this.notifications = [];
      this.notifCurrentPage = 0;
      try {
        await clientStore.dispatch("clearNotificationUnreceivedCount");
        this.unreadCount = 0;
      } catch (err) {
        console.error(err);
      } finally {
        this.notifReady = true;
      }
    },
    openNotificationItem(item) {
      if (!item) return;
      // Content notification (docs/notification-content-proposal.md §8.1): subject entry →
      // the form directly; otherwise the reader. The detail call marks it read.
      if (isContentNotification(item)) {
        item.read = true;
        // An approval request (docs/teammate-human-approval-request-proposal.md) opens the
        // request in the client shell, whose quick view serves its View-entry link; Lite only
        // opens entries from inside its list view.
        const approvalRequestId = approvalRequestIdOf(item);
        if (approvalRequestId) {
          clientStore.dispatch("retrieveNotificationDetail", item.id).catch(() => {});
          this.$router.push({ name: "approval-request", params: { id: approvalRequestId } });
          return;
        }
        if (item.about && item.about.locusId) {
          clientStore.dispatch("retrieveNotificationDetail", item.id).catch(() => {});
          this.onFormOpen({ entryId: item.about.locusId, moduleId: item.about.vectorId });
          return;
        }
        window.eventBus.$emit("notificationOpen", item.id);
        return;
      }
      if (!item.locusId) return;
      this.onFormOpen({ entryId: item.locusId, moduleId: item.vectorId });
    },
    onNotificationRead(id) {
      const row = this.notifications.find((x) => x.id === id);
      if (row) row.read = true;
    },
    async loadNotification() {
      try {
        const res = await clientStore.dispatch("retrieveNotification", {
          pageSize: 10,
          currentPage: this.notifCurrentPage,
        });

        if (res.data && res.data.list && res.data.list.length) {
          res.data.list.forEach((x) => {
            if (this.notifications.findIndex(y => y.id === x.id) === -1) {
              x.shortMessage = _.truncate(x.message, { 'length': 200 });
              this.notifications.push(x);
            }
          });

          return true;
        }
        return false;
      } catch (err) {
        console.error(err);
        return false;
      }
    },
    async loadNotificationCount() {
      let res2 = await clientStore.dispatch("retrieveNotificationCount");
      this.unreadCount = res2.data && res2.data.count;
    },
    // module
    createModule() {
      if (this.workspaceLoading) {
        return;
      }

      this.$router.push({ name: "lite-module-hub"});
    },
    async openModuleOnboardModal() {
      this.$refs.ftsModuleOnboardModal.show();
    },
    // tour
    startTour() {
      this.$tours.liteTour.start();
    },
    endTour() {
      $cookies.get('client_onboarding') && $cookies.remove('client_onboarding');
    },
    tourSteps() {
      return [...tours.liteTourSteps];
    },
    // run as
    async setRunAs(payload) {
      try {
        payload.loading && payload.loading(true);
        authorizationStore.dispatch("setRunAs", {
          workspaceId: this.workspaceId,
          target: payload.target
        });

        await clientStore.dispatch("loadWorkspace", {
          id: this.workspaceId,
          forceReload: true,
          preserveMenuState: true
        });

        if (this.$route.name == "dashboard") {
          window.eventBus.$emit("dashboardRefresh");
        } else if (this.$route.name == "calendar") {
          window.eventBus.$emit("calendarRefresh");
        } else if (this.$route.name == "list-view") {
          window.eventBus.$emit("listing:schemaUpdated");
        }
      } catch(err) {
        console.error(err);
        payload.error && payload.error(err.response && err.response.data && err.response.data.message || "Something went wrong");
      } finally {
        payload.loading && payload.loading(false);
      }
    },
    // workspace interval
    setWorkspaceVersionInterval() {
      if (this.workspaceVersionInterval) {
        clearInterval(this.workspaceVersionInterval);
        this.workspaceVersionInterval = null;
      }

      this.workspaceVersionInterval = setInterval(() => {
        if (this.inactivityStartTime) {
          const now = new Date;
          const downtime = now - this.inactivityStartTime;
          if (downtime > 3600000) {
            if (this.workspaceVersionInterval) {
              clearInterval(this.workspaceVersionInterval);
              this.workspaceVersionInterval = null;
            }
            return;
          }
        }

        if (!navigator.onLine) {
          return;
        }

        this.loadWorkspace({
          id: this.workspaceId,
          preserveMenuState: true,
          background: true
        }).catch((err) => console.error(err));
      }, 3600000);
    },
    async handleVisibilityChange() {
      if (document.hidden) {
        this.inactivityStartTime = new Date;

        if (authorizationStore.getters.isDevelopment) {
          console.info("Client:Inactive", `${moment(this.inactivityStartTime).format('YYYY-MM-DD HH:mm:ss')}`);
        }
      } else {
        const now = new Date;
        const downtime = now - this.inactivityStartTime;
        this.inactivityStartTime = null;

        if (authorizationStore.getters.isDevelopment) {
          const formatDowntime = (downtime) => {
            const duration = moment.duration(downtime);
            const hours = Math.floor(duration.asHours());
            const minutes = Math.floor(duration.minutes());
            const seconds = Math.floor(duration.seconds());
            return `${hours}h ${minutes}m ${seconds}s`;
          }
          console.info("Client:Active", `${moment(now).format('YYYY-MM-DD HH:mm:ss')}, downtime: ${formatDowntime(downtime)}`);
        }

        if (downtime > 3600000) {
          this.setWorkspaceVersionInterval();

          if (!navigator.onLine) {
            return;
          }

          this.loadWorkspace({
            id: this.workspaceId,
            preserveMenuState: true,
            background: true
          }).catch((err) => console.error(err));
        }
      }
    },
    // commons
    focusRef(refName) {
      this.$nextTick(() => {
        const el = this.$refs[refName];
        if (el) el.focus();
      });
    }
  },
  watch: {
    isLiteLobby: {
      immediate: true,
      handler(val) {
        if (val && this.control !== "web") {
          this.setControlState("web");
        }
      }
    },
    "$route.name"(val, oldVal) {
      if (
        oldVal === "lite-preview" &&
        val !== "lite-preview" &&
        this.control === "preview"
      ) {
        this.setControlState("web");
      }
    },
    workspaceId(val, oldVal) {
      if (val != oldVal) {
        this.previousWorkspaceId = oldVal;
        this.unreadCount = 0;
        this.loadNotificationCount();
        this.loadPreWorkspaces();
      }
    },
    workspaceSearch: _.debounce(async function () {
      this.workspacesSearching = true;
      await this.loadWorkspaceOptions(this.workspaceSearch);
    }, 500),
    workspaceContextSearch: _.debounce(async function () {
      this.contextOptionList = await clientStore.dispatch(
        "getWorkspaceContexts",
        { search: this.workspaceContextSearch}
      );
    }, 500),
    moduleId(val, oldVal) {
      if (oldVal != val && this.isAdminUser) {
        const designModuleId = canvasDesignStore.getters.vector && canvasDesignStore.getters.vector.id;
        if (designModuleId != val) {
          this.loadDesign({ moduleId: val });
        }
      }
    },
    isSidenavStatic(val) {
      if (val === true) {
        this.isFloatingMenuOpen = false;
      }
    },
    isAdminUser(val) {
      if (this.control === "design" && val === false) {
        this.setControl("web");
      }
    }
  },
  computed: {
    metaTitle() {
      let title = ['Inistate'];
      this.workspace.name && title.push(this.workspace.name);
      this.module.name && title.push(this.module.name);
      return title.reverse().join(' - ');
    },
    isUsersPage() {
      return this.$route?.meta?.group === "users";
    },
    isLiteLobby() {
      return this.$route.name === "lite-lobby";
    },
    isDesignMode() {
      return clientStore.getters.liteDesignMode;
    },
    isLiteMode() {
      return clientStore.state.isLiteMode;
    },
    sidenavLogo() {
      return authorizationStore.getters.appLogo;
    },
    designRoute() {
      let route = { name: 'studio', query: {}};
      if (this.module.id)
        route.query.id = commons.encodeids(this.module.id);
      else
        route.query.wsId = this.workspaceId;
      return route;
    },
    routePath() {
      if (this.$route.name === "lite-studio") {
        return this.$route.name;
      }

      return `${this.$route.path}/${this.$route.query.l}`;
    },
    workspace() {
      return clientStore.state.workspace || {};
    },
    workspaceId() {
      return clientStore.getters.workspaceId;
    },
    workspaceName() {
      return clientStore.getters.workspaceName;
    },
    workspaceModules() {
      return clientStore.getters.workspaceModules;
    },
    workspaceContext() {
      return clientStore.state.workspaceContext || {};
    },
    isWorkspaceContext() {
      return this.workspaceContext && this.workspaceContext.hasOwnProperty("id");
    },
    workspaceLobby() {
      return clientStore.getters.workspaceLobby;
    },
    workspaceModuleOptions() {
      let workspaceModules = this.workspaceModules;

      if (this.moduleId) {
        workspaceModules = workspaceModules.filter(x => x.id != this.moduleId);
      }

      if (this.workspaceModuleSearch) {
        return workspaceModules.filter(x => 
          x.name && x.name.toLowerCase().indexOf(this.workspaceModuleSearch.toLowerCase()) !== -1
        );
      }

      return workspaceModules;
    },
    workspaceNameSpecialCharacters() {
      return designValidator.workspaceNameSpecialCharacters.split("");
    },
    module() {
      return clientStore.state.module || {};
    },
    moduleId() {
      return clientStore.getters.moduleId;
    },
    moduleListingOptions() {
      let moduleMenus = Array.isArray(this.module.menus) ? this.module.menus : [];

      if (this.listId) {
        moduleMenus = moduleMenus.filter(x => x.id != this.listId && x.id != "everything");
      }

      if (this.moduleListingSearch) {
        return moduleMenus.filter(x => 
          x.name && x.name.toLowerCase().indexOf(this.moduleListingSearch.toLowerCase()) !== -1
        );
      }

      return moduleMenus;
    },
    list() {
      return clientStore.state.list || {};
    },
    listId() {
      return clientStore.getters.listId;
    },
    profile() {
      return authorizationStore.getters.userProfile;
    },
    userRoles() {
      return authorizationStore.getters.userRoles;
    },
    isAdminUser() {
      return authorizationStore.getters.isAdminUser;
    },
    // "Manage workspace" left the workspace menu once settings went live: the avatar is the one way
    // in (KY, 2026-10-01). It comes back only with the kill switch, as the old dialog's door.
    manageWorkspaceInMenu() {
      return !settingsStageAllowed();
    },
    isManagerUser() {
      return authorizationStore.getters.isManagerUser;
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    // Raw identity — stays true in demo mode so the demo toggle stays visible.
    isInternalIdentity() {
      return authorizationStore.getters.isInternalIdentity;
    },
    demoMode() {
      return authorizationStore.getters.demoMode;
    },
    isBeta() {
      return authorizationStore.getters.isBeta;
    },
    isAnySidebarMenuOpen() {
      return commons.isAnySidebarMenuOpen(clientStore.getters.workspaceMenus);
    },
    navigationOptions() { 
      let options = [];

      if (this.isManagerUser || this.isAdminUser) {
        options.push({ id: 'users', name: "User management", routeName: 'lite-users' });
      }

      return options;
    },
    breadcrumb() {
      let breadcrumb;

      if (["lite", "lite-studio"].includes(this.$route.name)) {
        if (this.module && this.module.id) {
          breadcrumb = {
            module: {
              id: this.module.id,
              emoji: this.module.emoji,
              name: this.module.name,
              active: true
            }
          }

          if (this.list && this.list.id && this.list.id != "everything") {
            breadcrumb.listing = {
              id: this.list.id,
              emoji: this.list.emoji,
              name: this.list.name,
              active: true
            }

            breadcrumb.module.active = false;
          }
        }
      }

      return breadcrumb;
    },
    controls() {
      const controls = [
        {
          id: "web",
          iconClass: 'material-icons-outlined',
          iconName: 'desktop_windows',
          name: "Web"
        },
        {
          id: "preview",
          iconClass: 'material-icons-outlined',
          iconName: 'phone_iphone',
          name: "App"
        }
      ];

      if (this.isLiteLobby) {
        controls.splice(1);
      } else if (this.isAdminUser) {
        controls.unshift({
          id: "design",
          iconClass: 'material-icons-outlined',
          iconName: 'edit',
          name: "Edit"
        });
      }

      if (this.hasNoActiveWorkspace) {
        const previewIndex = controls.findIndex(control => control.id === "preview");
        if (previewIndex !== -1) {
          controls.splice(previewIndex, 1);
        }
      }

      const floatingPositions = [
        { top: "-75px", left: "-5px" },
        { top: "-35px", left: "35px" },
        { top: "5px", left: "75px" }
      ];

      return controls.map((control, index) => ({
        ...control,
        floatingPosition: floatingPositions[index]
      }));
    }
  },
  async mounted() {
    // web app config
    await webAppConfig.getConfig();
    // log
    $cookies.set("loggedSource", "client");
    // reading preference: this person's own text size and contrast on this device (FTR00375)
    applyDisplay(authorizationStore.getters.userName);
    // ls.preference
    const localStoragePreference = {...LocalStorageService.getPreference()};    
    if (localStoragePreference.collapse) {
      this.isSidenavStatic = false;
    }
    // preference ready
    this.prefReady = true;

    let workspaceId = $cookies.get('wsId');

    if (this.$route.query && this.$route.query.wsId) {
      workspaceId = commons.decodeids(this.$route.query.wsId);
      let query = _.cloneDeep(this.$route.query);
      delete query.wsId;
      this.$router.replace({ query: query });
    }
    // profile
    await authorizationStore.dispatch("profile");
    // pdf document
    window.eventBus.$on("showPDFDocument", async (payload) => {
      let vm = this;

      // htmljs documents render through the new sandboxed viewer, never the legacy PdfSharp/HTML
      // path below. Branch first so all existing typeId handling is left exactly as-is.
      if (payload.document && payload.document.engine === "htmljs") {
        vm.$refs.htmlJsDocumentModal.show(payload);
        return;
      }

      let parameters = {
        documentId: payload.document.id,
        vectorId: payload.moduleId,
        locusIds:
          payload.items && payload.items.length
            ? payload.items.map((x) => x.id)
            : null,
        locusId:
          payload.items && payload.items.length ? payload.items[0].id : null,
        list: payload.listingId,
        queryFilters: payload.filters,
        inputs: payload.inputs,
      };

      let documentModel = { parameters: parameters };
      // the htmlViewer route reads the model back out of `file` after this exact prefix
      let prefixHTMLUrl =
        "/service/htmltemplateprocessing/document?documentmodel=";
      classificationStore.dispatch("updateDocumentId", null);

      if (
        payload.document.typeId == "spreadsheet" ||
        payload.document.typeId == "file"
      ) {

        let preparingToast = null;
        function addListeners(xhr) {
          xhr.addEventListener('loadstart', () => {
            // one toast for the download, also when it is asked again with a renewed token (SS05651)
            preparingToast = preparingToast || vm.$toasted.info("Preparing download");
          });
        }
        // SS06238: a failed download says so instead of stopping at "Preparing download"
        const downloadFailed = (detail) => {
          if (preparingToast && typeof preparingToast.goAway === "function") {
            preparingToast.goAway(0);
          }
          vm.$toasted.global.error({}, "Failed to download the document. Please try again.");
          console.error(detail);
        };

        documentModel.outputType = 4;
        // SS06238: GET as before; POST the model when the query string would be too long for IIS
        const documentRequest = buildDocumentRequest(PDF_DOCUMENT_PATH, documentModel);
        let url = documentRequest.url;
        /* SS05651: an expired access token is renewed and the download asked for again, as the shared
           HTTP client does for every other request; each attempt reads the token when it is sent. */
        const sendDownload = (answer) => {
          var xhr = new XMLHttpRequest();
          addListeners(xhr);
          xhr.open(
            documentRequest.method,
            webAppConfig.baseURL ? webAppConfig.baseURL + url : url,
            true
          );
          var token = JSON.parse(localStorage.getItem("ls.authorizationData"))[
            "access_token"
          ];
          xhr.setRequestHeader(
            "Content-type",
            documentRequest.method === "POST"
              ? DOCUMENT_POST_CONTENT_TYPE
              : "application/x-www-form-urlencoded"
          );
          xhr.setRequestHeader("authorization", "bearer " + token);
          xhr.setRequestHeader("wsId", authorizationStore.getters.runAsWsId || this.workspaceId);
          xhr.responseType = "arraybuffer";
          xhr.onload = () => answer(xhr);
          // network failure, or a CORS-blocked response
          xhr.onerror = function () {
            downloadFailed(`Document download failed (${xhr.status || "network error"})`);
          };
          xhr.send(documentRequest.body);
        };
        sendWithTokenRefreshCallback(sendDownload, () => authorizationStore.dispatch("refreshToken"), (xhr) => {
          if (xhr.status === 200) {
            var filename = "";
            var disposition = xhr.getResponseHeader("Content-Disposition");
            if (disposition && disposition.indexOf("attachment") !== -1) {
              var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
              var matches = filenameRegex.exec(disposition);
              if (matches != null && matches[1])
                filename = matches[1].replace(/['"]/g, "");
            }
            var type = xhr.getResponseHeader("Content-Type");

            var blob;
            if (typeof File === "function") {
              try {
                blob = new File([xhr.response], filename, { type: type });
              } catch (e) {
                /* Edge */
              }
            }
            if (typeof blob === "undefined") {
              blob = new Blob([xhr.response], { type: type });
            }

            if (typeof window.navigator.msSaveBlob !== "undefined") {
              /* IE workaround for "HTML7007: One or more blob URLs were revoked by closing the blob for which they were created. These URLs will no longer resolve as the data backing the URL has been freed." */
              window.navigator.msSaveBlob(blob, filename);
            } else {
              var URL = window.URL || window.webkitURL;
              var downloadUrl = URL.createObjectURL(blob);

              if (filename) {
                /* use HTML5 a[download] attribute to specify filename */
                var a = document.createElement("a");
                /* safari doesn't support this yet */
                if (typeof a.download === "undefined") {
                  window.location = downloadUrl;
                } else {
                  a.href = downloadUrl;
                  a.download = filename;
                  document.body.appendChild(a);
                  a.click();
                }
              } else {
                window.location = downloadUrl;
              }
              /* cleanup */
              setTimeout(function () {
                URL.revokeObjectURL(downloadUrl);
              }, 100);
            }
          } else {
            downloadFailed(`${xhr.status} (${xhr.statusText})`);
          }
        });
      } else if (payload.document.typeId == "pdf") {
        vm.$refs.documentModal.show({
          request: buildDocumentRequest(PDF_DOCUMENT_PATH, documentModel),
          parameters: {
            moduleId: payload.moduleId,
            templateId: payload.document.id,
            template: payload.document.template,
            entryId: payload.items && payload.items.length ? payload.items[0].id : null,
            entryIds: payload.items && payload.items.length
            ? payload.items.map((x) => x.id)
            : null,
            listingId: payload.listingId,
            filters: payload.filters,
            inputs: payload.inputs,
          }
        });
      } else {
        const encodedUri = prefixHTMLUrl + JSON.stringify(documentModel);
        window.open(`/#/htmlViewer?file=${encodeURIComponent(encodedUri)}`, "_blank");
      }
    });
    // signalr:refreshNotification
    window.eventBus.$on("refreshNotification", async () => {
      this.loadNotificationCount();
    });
    // form skip confirmation
    window.eventBus.$on("formSkipConfirmation", (payload) => {
      this.$refs.ftsFormSkipConfirmation.addQueue(payload);
    });
    // load design
    const loadDesign = async () => {
      if (this.$route.name === "lite" && this.$route.params.moduleId) {
        const moduleId = commons.decodeids(this.$route.params.moduleId);
        await this.loadDesign({ moduleId: moduleId });
      } else {
        await this.loadDesign({ workspaceId: this.workspaceId });
      }
    };
    // set content ready
    const setContentReady = () => {
      if (this.$route.name === "lite-preview") {
        this.setControl("preview");
      } else {
        const mode = LocalStorageService.getLiteMode(webAppConfig.baseServer);

        if (mode === "design") {
          this.setControl(this.isAdminUser ? "design" : "web");
        }
      }

      this.contentReady = true;
      // eventBus (DOM ready)
      // global event:open form
      window.eventBus.$on("app:form:open", this.onFormOpen);
      // mini studio handle
      window.eventBus.$on("design.mini-studio-open", this.navigateMiniStudio);
    }
    // load workspace
    let hasCache = false;
    let isDesignReady = false;
    const routeModuleId =
      this.$route.name === "lite" && this.$route.params && this.$route.params.moduleId
        ? commons.decodeids(this.$route.params.moduleId)
        : null;

    const isViewPage = !!routeModuleId;
    const queryDemoId = this.$route.query && this.$route.query.demoId ? this.$route.query.demoId : null;

    if (!isViewPage && !queryDemoId) {
      hasCache = await this.loadWorkspaceCache(workspaceId);
      if (hasCache !== false && this.isLiteMode) {
        try {
          if (this.isAdminUser) {
            await loadDesign();
            isDesignReady = true;
          }

          setContentReady();
        } catch(error) {
          LocalStorageService.clearWorkspace(webAppConfig.baseServer);
          console.error("Failed to load workspace design from workspace cache.", error);
        }
      }
    }

    try {
      const preserveMenuState = !!clientStore.getters.workspaceId;
      let workspacePayload = {
        id: workspaceId,
        demoId: queryDemoId,
        preserveMenuState: preserveMenuState
      };

      if (isViewPage && !queryDemoId) {
        workspacePayload = {
          moduleId: routeModuleId,
          preserveMenuState: preserveMenuState
        };
      }

      await this.loadWorkspace(workspacePayload, { isInitial: !hasCache });

      if (!this.isLiteMode) {
        this.$router.replace({ name: "dashboard" });
        return;
      }

      if (this.isAdminUser && !isDesignReady) {
        await loadDesign();
      }
    } catch(err) {
      if (err && err.status === 404) {
        this.hasNoActiveWorkspace = true;

        if (this.$route.name !== "lite") {
          this.$router.replace({ name: "lite" });
        }

        this.$nextTick(() => {
          this.$refs.workspaceCreateModal.open();
        });
      } else {
        authorizationStore.dispatch("logout");
        throw new Error(err);
      }
    }
    // load default workspace context
    if (this.workspace.hasContext && !this.isWorkspaceContext) {
      await this.setDefaultWorkspaceContext();
    }
    // back from Stripe Checkout → open the Plan & Credits tab (not awaited: a modal, not a step)
    this.openBillingReturn();
    // onboarding:redirect to list view
    let clientOnboarding = false;
    if (this.$route.query.redirect === 'list') {
      clientOnboarding = true;
      try {
        let defaultModule = null;
        const getDefaultModule = (list) => {
          return list.find((x) => {
            if (x.type === 'group') {
              return Array.isArray(x.children) && getDefaultModule(x.children);
            } else {
              defaultModule = x;
              return true;
            }
          });
        }
        Array.isArray(clientStore.getters.workspaceMenus) && getDefaultModule(clientStore.getters.workspaceMenus);
        if (defaultModule) {
          const route = commons.clientListViewRoute({
            moduleId: defaultModule.id,
            listId: Array.isArray(defaultModule.children) && defaultModule.children[0] && defaultModule.children[0].id
          });
          this.$router.replace(route);
        }
      } catch(err) { console.error(err); }
    }
    // content ready
    if (this.contentReady != true) {
      setContentReady();
    }
    // onboarding:tour
    if (clientOnboarding) {
      $cookies.set("client_onboarding", 1, "1y");
      this.$nextTick(() => {
        this.openModuleOnboardModal();
      });
    } else if (this.$route.query.tour == true || $cookies.get('client_onboarding') && $cookies.get('client_onboarding') == 1) {
      this.$nextTick(() => {
        this.startTour();
      });
    }
  },
  beforeDestroy() {
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    // reset run as
    if (!!this.isRunAs) {
      authorizationStore.dispatch("setRunAs");
    }
    // reset client state
    clientStore.dispatch("resetState");
    // reset theme
    document.body.classList.remove("dark");
    // clear interval
    if (this.workspaceVersionInterval) {
      clearInterval(this.workspaceVersionInterval);
      this.workspaceVersionInterval = null;
    }
    // clear timeout
    clearTimeout(this.workspaceChangedTimeout);
    clearTimeout(this.userChangedTimeout);
    clearTimeout(this.currentUserChangedTimeout);
    // off eventBus
    window.eventBus.$off("app:form:open", this.onFormOpen);
    window.eventBus.$off("design.mini-studio-open", this.navigateMiniStudio);
    window.eventBus.$off("signalr.workspaceChanged");
    window.eventBus.$off("signalr.userChanged");
    window.eventBus.$off("refreshNotification");
    window.eventBus.$off("showPDFDocument");
    window.eventBus.$off("formSkipConfirmation");
  },
  beforeRouteUpdate(to, from, next) {
    if (this.workspaceLobby && to.name !== "lite-lobby") {
      next({ name: "lite-lobby" });
    } else {
      next();
    }
  }
};
</script>

<style lang="scss">
@use "../src/assets/scss/vars.scss" as *;

.v-step[data-v-54f9a632]{
  background-color: var(--fts-primary-900);
}
.v-step__header[data-v-54f9a632]{
  background-color: var(--fts-primary-900);
  font-weight: var(--inistate-font-weight-bold);
  font-size: 20px;
  text-align: left;
  padding: 16px 16px 0px 16px;
}
.v-step__content[data-v-54f9a632]{
  padding: 0px;
  text-align: left;
}
.v-step__arrow--dark[data-v-54f9a632]:before{
  background-color: var(--fts-primary-900);
}
.v-step__buttons[data-v-54f9a632]{
display: flex;
  justify-content: space-between; 
  align-items: center;            
  margin-top: 12px;               
  gap: 8px;
}
.v-step__button-skip {
  border: none !important;      
}
.v-step__button-next {
  border: #fff 1px solid;      
  border-radius: 4px;
}

.layout {
  height: 100%;
  display: flex;
  background-color: var(--inistate-color-shade-white);
  .layout__sidenav {
    flex: 1 0 310px;
    width: 310px;
    max-width: 310px;
    display: flex;
    flex-direction: column;
    border-right: 1px solid rgba(var(--inistate-color-neutral-200-rgb), 0.5);
    background-color: rgba(var(--inistate-color-neutral-50-rgb), 0.5);
    transition: all 0.3s ease;
    &.layout__sidenav--collapsed {
      position: fixed;
      top: 0;
      bottom: 0;
      transform: translate(-100%, 0);
      z-index: calc(#{$z-index-fixed} + 1);
    }
  }
  .layout__main {
    flex: 1;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .layout__topnav {
    display: flex;
    align-items: center;
    height: 56px;
    background-color: var(--inistate-color-shade-white);
  }
  .layout__content {
    flex: 1;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: auto;
    background-color: var(--inistate-color-shade-white);
  }

  .sidenav__header {
    padding-bottom: 0.75rem;
  }
  .sidenav__body {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  .sidenav__footer {
    padding-top: 16px;
  }

  .sidenav__divider {
    margin-left: 8px;
    margin-right: 8px;
    border-bottom: 1px solid rgba(var(--inistate-color-neutral-200-rgb), 0.5);
  }

  .sidenav__header-top {
    display: flex;
    align-items: flex-start;
    padding-top: 10px;
    padding-bottom: 10px;
    padding-left: 16px;
    padding-right: 8px;
  }
  .sidenav__header-top-left {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: flex-start;
    margin-right: 8px;
  }

  .sidenav__workspace {
    min-width: 0;
    flex: 1;
    .fts-form-control {
      margin-top: 1px;
      margin-bottom: 1px;
      padding-left: 7px;
      padding-right: 7px;
      border-radius: var(--inistate-rounded-4);
      color: inherit;
    }
  }
  .sidenav__workspace-toggle {
    min-height: 36px;
    display: flex;
    align-items: center;
    padding-left: 8px;
    padding-right: 8px;
    border-radius: var(--inistate-rounded-4);
    background-color: transparent;
    transition: background-color 0.3s ease;
    > * {
      @include text-overflow-ellipsis;
    }
    &.sidenav__workspace-toggle--editable {
      @include user-select-none;
      &:hover {
        background-color: var(--inistate-color-neutral-100);
        cursor: pointer;
      }
    }
  }
  .sidenav__workspace-label {
    font-weight: var(--inistate-font-weight-bold);
    font-size: var(--inistate-heading-text-16);
    line-height: var(--inistate-heading-text-16--line-height);
    color: var(--inistate-text-primary);
  }
  .sidenav__workspace-hint {
    margin-top: 6px;
    font-size: var(--inistate-label-text-10);
    line-height: var(--inistate-label-text-10--line-height);
    color: var(--inistate-text-tertiary);
  }
  .sidenav__workspace-error {
    color: var(--inistate-text-error);
  }

  .sidenav__workspace-menu-toggle {
    margin-left: 6px;
    .sidenav__toggle > span {
      transition: transform 0.3s ease;
    }
    &.sidenav__workspace-menu-toggle--active {
      .sidenav__toggle > span {
        transform: rotate(-180deg);
      }
    }
  }

  .sidenav__toggle {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 36px;
    width: 36px;
    border-radius: var(--inistate-rounded-4);
    background-color: transparent;
    color: var(--inistate-color-neutral-500);
    transition: background-color 0.3s ease, color 0.3s ease;
    @include user-select-none;
    > span {
      font-size: var(--inistate-heading-text-20);
    }
    &:hover {
      background-color: var(--inistate-color-neutral-100);
      color: var(--inistate-text-primary);
      cursor: pointer;
    }
    &.sidenav__toggle--active {
      color: var(--inistate-text-primary);
    }
  }

  .sidenav__filter {
    > .fts-form-control {
      border-radius: var(--inistate-rounded-4);
    }
    .search-icon {
      display: flex;
      align-items: center;
      > span {
        font-size: 16px;
        color: var(--inistate-text-primary);
      }
    }
  }

  .sidenav__header-bottom {
    display: flex;
    align-items: center;
    min-height: 34px;
    margin-top: 16px;
    margin-left: 8px;
    margin-right: 8px;
    .sidenav__filter {
      flex: 1;
      margin-right: 12px;
    }
  }
  .sidenav__module-label {
    flex-shrink: 0;
    flex: 1;
    padding-left: 16px;
    color: var(--inistate-text-secondary);
    font-weight: var(--inistate-font-weight-medium);
    font-size: var(--inistate-paragraph-text-14);
    line-height: var(--inistate-paragraph-text-14--line-height);
    cursor: default;
  }
  .sidenav__action-container {
    display: flex;
    align-items: center;
    > *:not(:last-child) {
      margin-right: 0.25rem;
    }
  }
  .sidenav__action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 24px;
    width: 24px;
    border-radius: var(--inistate-rounded-4);
    background-color: transparent;
    color: var(--inistate-color-neutral-500);
    transition: background-color 0.3s ease, color 0.3s ease;
    @include user-select-none;
    > span {
      font-size: 16px;
    }
    &:hover {
      background-color: var(--inistate-color-neutral-100);
      color: var(--inistate-text-primary);
      cursor: pointer;
    }
  }

  .sidenav__action-btn--disabled {
    pointer-events: none;
    opacity: 0.5;

    &:hover {
      background-color: transparent;
      color: var(--inistate-color-neutral-500);
      cursor: default;
    }
  }

  .sidenav__header-content {
  }

  .sidenav__profile-container {
    display: flex;
    align-items: center;
    padding: 1rem;
  }
  .sidenav__profile {
    flex: 1;
    margin-right: 1rem;
    cursor: pointer;
  }
  .sidenav__profile-name {
    color: var(--inistate-text-primary);
    font-weight: var(--inistate-font-weight-medium);
    font-size: var(--inistate-paragraph-text-16);
    line-height: var(--inistate-paragraph-text-16--line-height);
  }
  .sidenav__profile-username {
    color: var(--inistate-text-secondary);
    font-weight: var(--inistate-font-weight-light);
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-paragraph-text-14--line-height);
  }
  .sidenav-settings {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 36px;
    width: 36px;
    border-radius: var(--inistate-rounded-4);
    background-color: transparent;
    color: var(--inistate-color-neutral-500);
    transition: background-color 0.3s ease, color 0.3s ease;
    @include user-select-none;
    > span {
      font-size: 20px;
    }
    &:hover {
      background-color: var(--inistate-color-neutral-100);
      color: var(--inistate-text-primary);
      cursor: pointer;
    }
  }

  .sidenav__module-container {
    flex: 1;
  }

  .topnav__left {
    min-width: 0;
    display: flex;
    align-items: center;
    flex: 1;
    padding-left: 1rem;
    padding-right: 1rem;
  }
  .topnav__right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1;
    padding-left: 1rem;
    padding-right: 1rem;

    .fts-btn{
      border-radius: 4px !important;
    }
  }

  .topnav__breadcrumb {
    min-width: 0;
    display: flex;
    align-items: center;
    color: var(--inistate-text-placeholder);
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-label-text-14--line-height);
  }
  .topnav__breadcrumb-item-wrapper {
    min-width: 70px;
  }
  .topnav__breadcrumb-item {
    @include user-select-none;
    min-width: 0;
    display: flex;
    align-items: center;
    padding: 6px 8px;
    border-radius: var(--inistate-rounded-8);
    background-color: transparent;
    transition: background-color ease 0.3s;

    .label-group__label {
      color: inherit;
    }

    .topnav__breadcrumb-item-icon {
      height: 20px;
      width: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: 2px;
      color: var(--inistate-icon-secondary);
      transition: color ease 0.3s;
      > span {
        transition: transform 0.3s ease;
        font-size: var(--inistate-label-text-16);
      }

      &:hover {
        color: var(--inistate-icon-primary);
      }
    }

    &.topnav__breadcrumb-item--active {
      .label-group__label {
        color: var(--inistate-text-primary);
      }
    }

    &:hover {
      background-color: var(--inistate-background-secondary);
      cursor: pointer;
    }
  }
  .topnav__breadcrumb-dropdown-toggle {
    &.topnav__breadcrumb-dropdown-toggle--expanded {
      .topnav__breadcrumb-item-icon > span {
        transform: rotate(-180deg);
      }
    }
  }
  .topnav__breadcrumb-divider {
    margin-left: 6px;
    margin-right: 6px;
  }
  .topnav__breadcrumb-action {
    flex-shrink: 0;
    height: 36px;
    width: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 6px;
    border-radius: var(--inistate-rounded-4);
    background-color: transparent;
    color: var(--inistate-icon-secondary);
    font-size: var(--inistate-label-text-12);
    transition: background-color 0.3s ease;

    &:hover {
      background-color: var(--inistate-background-brand-light);
      cursor: pointer;
    }
  }

  .topnav_sidenav-toggle {
    margin-right: 0.5rem;
  }

  .topnav__title-wrapper {
    min-width: 0;
    display: flex;
    align-items: center;

    .topnav__title:not(:first-child) {
      margin-left: 10px;
    }
  }

  .topnav__logo {
    flex-shrink: 0;
    height: 24px;
    width: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--inistate-rounded-4);
    > img {
      border-radius: var(--inistate-rounded-4);
      height: 100%;
      width: 100%;
    }
  }

  .topnav__title {
    @include text-overflow-ellipsis;
    font-weight: var(--inistate-font-weight-bold);
    font-size: var(--inistate-heading-text-20);
    line-height: var(--inistate-heading-text-20--line-height);
    color: var(--inistate-text-primary);
    cursor: default;
  }

  .topnav__control {
    display: inline-flex;
    align-items: center;
    margin: 16px 8px;
    padding: 6px;
    border-radius: var(--inistate-rounded-4);
    background-color: transparent;
    transition: background-color 300ms ease, color 200ms ease;
    @include user-select-none;
  }
  .topnav__control-item {
    height: 36px;
    min-width: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
    border-radius: var(--inistate-rounded-4);
    font-weight: var(--inistate-font-weight-medium);
    font-size: var(--inistate-paragraph-text-14);
    line-height: var(--inistate-paragraph-text-14--line-height);
    background-color: transparent;
    color: var(--inistate-color-neutral-500);
    cursor: pointer;
    transition: background-color 300ms ease, color 200ms ease;
    > span {
      font-size: var(--inistate-paragraph-text-16);
    }
    &:hover {
      background-color: var(--inistate-color-neutral-100);
      color: var(--inistate-text-secondary);
    }
    &.topnav__control-item--active {
      background-color: var(--inistate-color-primary-500);
      color: var(--inistate-color-shade-white);
    }
    &.topnav__control-item--active-outline {
      background-color: var(--inistate-color-primary-100);
      color: var(--inistate-color-primary-500);
    }
  }
  .control-item-label {
    margin-left: 0.375rem;
  }

  .fab-control {
    position: fixed;
    left: 1rem;
    bottom: 93px;
  }
  .fab-control-toggle {
    > span {
      font-size: var(--inistate-paragraph-text-16); 
    }
  }
  .fab-control-item {
    position: absolute;
    min-width: 36px;
    min-height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    background-color: var(--inistate-color-neutral-50);
    border-radius: 50%;
    font-size: var(--inistate-paragraph-text-14);
    transition: top 0.3s ease, bottom 0.3s ease, left 0.3s ease, right 0.3s ease, opacity 0.3s ease;
    opacity: 0;
    cursor: pointer;
    > span {
      font-size: var(--inistate-paragraph-text-16);
    }
    &:hover {
      border-radius: var(--inistate-rounded-8);
      .fab-control-item-label {
        display: block;
      }
    }
  }
  .fab-control-item-label {
    display: none;
    margin-left: 0.25rem;
  }
}

.inistate-lite {
  $design-background-color: rgba(246, 193, 119);
  &.inistate-lite--design {
    .layout__sidenav {
      background-color: var(--inistate-color-shade-white);
    }
    .layout__topnav {
      background-color: rgba($design-background-color, 0.1);
    }
    // Calm density P1 (R10a, 2026-09-05): the design-mode wash stays on the topnav only, so
    // the content the designer is judging looks like the content the user will see.
    .topnav__control {
      background-color: var(--inistate-color-neutral-50);
    }
    .topnav__control-item {
      &.topnav__control-item--active {
        background-color: $design-background-color;
        color: var(--inistate-color-neutral-900);
      }
    }
  }
  // override
  .fts-comment .comment-wrapper .comment-content {
    border-color: var(--inistate-border-primary);
    border-radius: var(--inistate-rounded-4);
    background-color: var(--inistate-color-shade-white);
  }
}

/* ==========================================================================
   Calm density — P1 (2026-09-05), the shell: sidenav + topnav + collapsed FAB
   docs/frontend-lite-shell-calm-density-proposal.md, rulings S1–S10 (recommended options).
   Class-level only: nothing added, removed or re-parented; "MODULES", the action-button order
   and every hook the Playwright suite uses survive. Each block names its ruling.
   ========================================================================== */
.inistate-lite.layout {

  /* ---- S1 · rail: 280px, white, one hairline ---- */
  .layout__sidenav {
    flex-basis: 280px;
    width: 280px;
    max-width: 280px;
    background-color: var(--inistate-color-shade-white);
    border-right: 1px solid var(--fts-gray-200);
  }

  /* ---- S2 · header: 48px, one line ---- */
  .sidenav__header { padding-bottom: 0; }
  .sidenav__header > .sidenav__divider { display: none; }
  .sidenav__header-top {
    min-height: 48px;
    align-items: center;
    padding: 0 8px 0 16px;
    border-bottom: 1px solid var(--fts-gray-100);
  }
  .sidenav__header-top-left { align-items: center; margin-right: 4px; }
  .sidenav__workspace-toggle {
    min-height: 28px;
    padding: 0 4px;
    margin-left: -4px;
  }
  .sidenav__workspace-label {
    font-size: 0.8125rem;
    line-height: 20px;
    font-weight: 600;
    color: var(--fts-text);
  }
  .sidenav__workspace .fts-form-control {
    height: 32px;
    min-height: 32px;
    padding: 0 8px;
    font-size: 0.8125rem;
  }
  .sidenav__toggle {
    height: 32px;
    width: 32px;
    color: var(--fts-default);
    > span { font-size: 18px; }
    &:hover { background-color: transparent; color: var(--fts-text); }
  }
  .sidenav__workspace-menu-toggle {
    margin-left: 0;
    .sidenav__toggle {
      height: 24px;
      width: 24px;
      > span { font-size: 16px; }
    }
  }

  /* ---- S3 · modules row: group label, actions at rest 0.35 ---- */
  .sidenav__header-bottom {
    min-height: 32px;
    margin: 8px 8px 0;
    padding: 0 4px 0 8px;
    &:hover .sidenav__action-btn { opacity: 1; }
    &:has(.sidenav__filter) .sidenav__action-btn { opacity: 1; }
    .sidenav__filter { margin-right: 4px; }
  }
  .sidenav__module-label {
    padding-left: 0;
    font-size: 0.625rem;
    line-height: 16px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--fts-form-helper);
  }
  .sidenav__action-btn {
    color: var(--fts-default);
    opacity: 0.35;
    transition: opacity 0.15s ease, color 0.15s ease;
    &:hover { opacity: 1; background-color: transparent; color: var(--fts-text); }
  }
  .sidenav__filter {
    > .fts-form-control {
      height: 32px;
      min-height: 32px;
      padding: 0 28px 0 8px;
      font-size: 0.8125rem;
      border-color: var(--fts-gray-200);
    }
    .search-icon > span { font-size: 16px; color: var(--fts-default); }
  }

  /* ---- S4 / S5 · module rows 32px, current = weight; view rows 28px ---- */
  .fts-sidenav-module {
    .module-wrapper {
      min-height: 32px;
      margin: 0 8px 1px;
      padding-top: 0;
      padding-bottom: 0;
      padding-right: 8px;
      border-radius: var(--inistate-rounded-4);
      font-size: 0.8125rem;
      line-height: 20px;
      color: var(--fts-text);
      &:hover {
        background-color: var(--inistate-color-neutral-50);
        > .module-info { font-weight: 400; }
      }
      &.active {
        background-color: var(--inistate-color-neutral-100);
        > .module-info { font-weight: 600; }
      }
      .module-info .module-name-wrapper { min-height: 20px; }
      .module-toggle {
        font-size: 11px;
        color: var(--fts-default);
      }
      .module-action {
        height: 24px;
        width: 24px;
        color: var(--fts-default);
        > span { font-size: 16px; }
        &:hover { background-color: transparent; color: var(--fts-text); }
      }
      .module-tag {
        margin-left: 6px;
        padding: 0;
        font-size: 0.625rem;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        background-color: transparent;
        color: var(--fts-default);
      }
      &.module-wrapper--admin {
        color: var(--fts-text);
        &:hover { background-color: var(--inistate-color-neutral-50); }
        &.active { background-color: var(--inistate-color-neutral-100); }
        &:hover::before, &.active::before { content: none; }
      }
    }
    /* view rows (listings under a module) — the NESTED tree only. A top-level module without
       views also renders as an <a>, so the selector keys on nesting, not on the tag. */
    .fts-sidenav-module .module-wrapper {
      min-height: 28px;
      font-size: 0.75rem;
      color: var(--inistate-text-secondary);
      &.active { color: var(--fts-text); }
      .module-emoji { font-size: 12px; }
    }
  }

  /* ---- S6 · footer controls: a 40px ghost row, active = ink underline; design keeps the amber ---- */
  .sidenav__footer { padding-top: 0; }
  .sidenav__footer > .sidenav__divider { display: none; }
  .topnav__control {
    display: flex;
    align-items: center;
    gap: 2px;
    margin: 0;
    padding: 4px 8px;
    border-radius: 0;
    border-top: 1px solid var(--fts-gray-100);
    background-color: transparent !important;
  }
  .topnav__control-item {
    position: relative;
    height: 32px;
    min-width: 32px;
    padding: 0 6px;
    font-size: 0.75rem;
    color: var(--fts-default);
    > span { font-size: 18px; }
    &:hover { background-color: transparent; color: var(--fts-text); }
    &.topnav__control-item--active {
      background-color: transparent;
      color: var(--fts-text);
      &::after {
        content: "";
        position: absolute;
        left: 8px;
        right: 8px;
        bottom: 2px;
        height: 2px;
        background-color: var(--fts-text);
      }
    }
    &.topnav__control-item--active-outline {
      background-color: transparent;
      color: var(--fts-text);
    }
  }
  .control-item-label { margin-left: 4px; }

  /* ---- S7 · profile row 48px ---- */
  .sidenav__profile-container {
    min-height: 48px;
    padding: 0 8px 0 16px;
    border-top: 1px solid var(--fts-gray-100);
  }
  .sidenav__profile { margin-right: 8px; }
  .sidenav__profile .profile-photo {
    width: 24px !important;
    height: 24px !important;
    min-width: 24px !important;
  }
  .sidenav__profile-name {
    font-size: 0.8125rem;
    line-height: 16px;
    font-weight: 500;
  }
  .sidenav__profile-username {
    font-size: 0.75rem;
    line-height: 16px;
    font-weight: 400;
  }
  .sidenav-settings {
    height: 24px;
    width: 24px;
    color: var(--fts-default);
    > span { font-size: 16px; }
    &:hover { background-color: transparent; color: var(--fts-text); }
  }

  /* ---- S8 · topnav 48px, breadcrumb as a path ---- */
  .layout__topnav {
    height: 48px;
    border-bottom: 1px solid var(--fts-gray-100);
  }
  .topnav__left { padding-left: 16px; padding-right: 8px; }
  .topnav__right { padding-right: 12px; }
  .topnav__breadcrumb {
    font-size: 0.8125rem;
    line-height: 20px;
    color: var(--inistate-text-secondary);
  }
  .topnav__breadcrumb-item-wrapper { min-width: 0; }
  .topnav__breadcrumb-item {
    height: 28px;
    padding: 0 6px;
    border-radius: var(--inistate-rounded-4);
    font-weight: 500;
    .label-group__label { color: inherit; }
    .topnav__breadcrumb-item-icon {
      height: 16px;
      width: 16px;
      color: var(--fts-default);
      > span { font-size: 16px; }
    }
    &.topnav__breadcrumb-item--active {
      font-weight: 600;
      .label-group__label { color: var(--fts-text); }
    }
    &:hover { background-color: var(--inistate-color-neutral-50); }
  }
  .topnav__breadcrumb-divider {
    margin: 0 2px;
    color: var(--fts-gray-300);
  }
  .topnav__breadcrumb-action {
    height: 24px;
    width: 24px;
    margin-left: 4px;
    color: var(--fts-default);
    opacity: 0.35;
    transition: opacity 0.15s ease;
    &:hover { opacity: 1; background-color: transparent; color: var(--fts-text); }
  }
  .topnav__title {
    font-size: 0.9375rem;
    line-height: 20px;
    font-weight: 600;
  }
  .topnav__logo { height: 20px; width: 20px; }
  .topnav__right .fts-btn {
    height: 32px;
    width: 32px;
    min-height: 32px;
    padding: 0;
    color: var(--fts-default);
    &:hover { color: var(--fts-text); }
  }

  /* ---- S9 · collapsed rail: the floating mode control on white ---- */
  .fab-control-toggle {
    background-color: var(--inistate-color-shade-white) !important;
    border: 1px solid var(--fts-gray-200) !important;
    color: var(--fts-text) !important;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.07), 0 3px 6px -4px rgba(0, 0, 0, 0.12) !important;
  }
  .fab-control-item {
    background-color: var(--inistate-color-shade-white);
    border: 1px solid var(--fts-gray-200);
    color: var(--fts-text);
    font-size: 0.75rem;
  }
}

/* design mode: the amber stays on the topnav (R10a) and on the active mode control only */
.inistate-lite.layout.inistate-lite--design {
  .topnav__control { background-color: transparent !important; }
  .topnav__control-item.topnav__control-item--active {
    background-color: rgba(246, 193, 119, 0.28);
    color: var(--fts-text);
  }
}
</style>
